import * as React from 'react'
import { useSearchParams } from 'react-router-dom'
import Box from '@mui/joy/Box'
import Button from '@mui/joy/Button'
import FormControl from '@mui/joy/FormControl'
import FormLabel from '@mui/joy/FormLabel'
import Input from '@mui/joy/Input'
import Sheet from '@mui/joy/Sheet'
import Table from '@mui/joy/Table'
import Typography from '@mui/joy/Typography'
import Chip from '@mui/joy/Chip'
import Select from '@mui/joy/Select'
import Option from '@mui/joy/Option'
import Tooltip from '@mui/joy/Tooltip'
import IconButton from '@mui/joy/IconButton'
import Modal from '@mui/joy/Modal'
import ModalDialog from '@mui/joy/ModalDialog'
import SearchIcon from '@mui/icons-material/Search'
import RefreshIcon from '@mui/icons-material/Refresh'
import VisibilityIcon from '@mui/icons-material/Visibility'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import CancelRoundedIcon from '@mui/icons-material/CancelRounded'
import ArrowRightAltRoundedIcon from '@mui/icons-material/ArrowRightAltRounded'
import FiberManualRecordRoundedIcon from '@mui/icons-material/FiberManualRecordRounded'
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded'
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined'
import { CircularProgress } from '@mui/joy'
import { useColorScheme } from '@mui/joy/styles'
import axios from 'axios'
import MDEditor from '@uiw/react-md-editor'
import { formatToUserLocalTime } from '@/utils/date-formatting'
import {
    auditDayOf,
    auditDayRange,
    type DocumentJobDetailRecord,
    type DocumentJobListItem,
    DocumentJobOutcome,
    type DocumentJobRecord,
    DocumentJobStatus,
    DOCUMENT_JOB_END_STATUSES,
    DOCUMENT_JOB_FAILED_STATUSES,
    DocumentMode,
    DocumentProcessingStep,
} from '@numeo/types-core'
import { STATUS_HINTS } from '../document-pipeline-audit/hints'
import { timeZoneName, VIEWER_TIME_ZONE } from '../document-pipeline-audit/viewerTimeZone'
import { FAILED_STATUS_FILTER } from './statusFilter'
import { PIPELINE_PRODUCER_LABELS } from '../document-pipeline-audit/api'
import { formatAuditDay } from '../document-pipeline-audit/format'

// The server's contracts, declared once in @numeo/types; dates arrive as ISO strings.
type DocumentJobRow = DocumentJobRecord
type DocumentJobListRow = DocumentJobListItem

/** The audit detail payload — the job row plus its extraction artifact (if any), a signed preview URL, and raw OCR text. */
interface DocumentJobDetailResponse {
    job: DocumentJobDetailRecord
    previewUrl: string | null
    ocrText: string | null
}

/** Sniff PDF vs image from the object path (before any query string) — `blob_uri`/`filename` both carry the real extension. */
function isImagePath(path: string | null | undefined): boolean {
    const clean = (path ?? '').split('?')[0].toLowerCase()
    return clean.endsWith('.jpg') || clean.endsWith('.jpeg') || clean.endsWith('.png')
}

interface DocumentJobEvent {
    id: number
    job_id: string
    at: string
    from_status: string | null
    to_status: string
    step: string | null
    actor: string | null
    detail: Record<string, unknown> | null
}

const PAGE_SIZE = 50

const statusColors: Record<string, 'success' | 'danger' | 'warning' | 'neutral' | 'primary'> = {
    [DocumentJobStatus.UPLOAD_RUNNING]: 'primary',
    [DocumentJobStatus.DUPLICATE_WAITING]: 'neutral',
    [DocumentJobStatus.OCR_QUEUED]: 'warning',
    [DocumentJobStatus.OCR_LEASED]: 'warning',
    [DocumentJobStatus.OCR_DONE]: 'primary',
    [DocumentJobStatus.OCR_NO_TEXT]: 'primary',
    [DocumentJobStatus.OCR_FAILED]: 'danger',
    [DocumentJobStatus.LLM_LEASED]: 'primary',
    [DocumentJobStatus.LLM_DONE]: 'success',
    [DocumentJobStatus.LLM_NOT_OF_INTEREST]: 'neutral',
    [DocumentJobStatus.LLM_COMBINED]: 'warning',
    [DocumentJobStatus.LLM_FAILED]: 'danger',
}

/** A stable color per batch, so rows submitted together read as one group. */
const batchColor = (batchId: string): string => {
    let hash = 0
    for (let i = 0; i < batchId.length; i++) hash = (hash * 31 + batchId.charCodeAt(i)) | 0
    return `hsl(${Math.abs(hash) % 360} 70% 45%)`
}

const formatTokens = (value: string | number | null | undefined): string => {
    const n = Number(value ?? 0)
    return n >= 1000 ? `${(n / 1000).toFixed(n >= 100_000 ? 0 : 1)}k` : String(n)
}

/** What the date chip says: an audit day ("Wed, Oct 7, 2026 (CT)") when the range is exactly one of the viewer's days, else the range. */
function enqueuedRangeLabel(since: string, until: string): string {
    if (since && until) {
        const day = auditDayOf(new Date(since), VIEWER_TIME_ZONE)
        const range = auditDayRange(day, VIEWER_TIME_ZONE)
        if (range.start.toISOString() === since && range.end.getTime() - 1 === new Date(until).getTime())
            return `enqueued ${formatAuditDay(day)} (${timeZoneName(VIEWER_TIME_ZONE, true)})`
        return `enqueued ${formatToUserLocalTime(since)} – ${formatToUserLocalTime(until)}`
    }
    if (since) return `since ${formatToUserLocalTime(since)}`
    return `until ${formatToUserLocalTime(until)}`
}

const truncateText = (text: string | null | undefined, maxLength = 50): string => {
    if (!text) return 'N/A'
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text
}

const stepLabels: Record<string, string> = {
    [DocumentProcessingStep.ENQUEUE]: 'Enqueue',
    [DocumentProcessingStep.DEDUP_LOOKUP]: 'Dedup Lookup',
    [DocumentProcessingStep.BLOB_UPLOAD]: 'Blob Upload',
    [DocumentProcessingStep.OCR]: 'OCR',
    [DocumentProcessingStep.CLASSIFY]: 'Classify',
    [DocumentProcessingStep.EXTRACT]: 'Extract',
    [DocumentProcessingStep.BATCH_SUBMIT]: 'Batch Submit',
    [DocumentProcessingStep.BATCH_POLL]: 'Batch Poll',
    [DocumentProcessingStep.RECONCILE]: 'Reconcile',
    [DocumentProcessingStep.PERSIST]: 'Persist',
    [DocumentProcessingStep.NOTIFY]: 'Notify',
    [DocumentProcessingStep.UNKNOWN]: 'Step',
}

type Tone = 'success' | 'danger' | 'warning' | 'neutral' | 'primary'

/** "420ms" / "6.4s" / "2m 13s" — compact, no more precision than is useful at each scale. */
function formatDuration(ms: number): string {
    if (ms < 1000) return `${Math.round(ms)}ms`
    if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`
    const minutes = Math.floor(ms / 60_000)
    const seconds = Math.round((ms % 60_000) / 1000)
    return `${minutes}m ${seconds}s`
}

/** The outcome filter's labels, keyed by the shared enum. */
const OUTCOME_LABELS: Record<DocumentJobOutcome, string> = {
    [DocumentJobOutcome.SHADOW_AGREED]: 'Shadow: agreed with legacy',
    [DocumentJobOutcome.SHADOW_MISMATCHED]: 'Shadow: fields differ',
    [DocumentJobOutcome.SHADOW_LEGACY_NONE]: 'Shadow: legacy found no ratecon',
    [DocumentJobOutcome.SHADOW_LEGACY_PENDING]: 'Shadow: legacy still pending',
    [DocumentJobOutcome.HANDLER_GAVE_UP]: 'Result handler gave up',
}

const failedStatuses = new Set<string>(DOCUMENT_JOB_FAILED_STATUSES)
/** Statuses that end a job before (or without) the extraction reading it: failures, junk, and a file that holds several documents. */
const failureToStatuses = new Set<string>([...failedStatuses, DocumentJobStatus.LLM_NOT_OF_INTEREST, DocumentJobStatus.LLM_COMBINED])
const terminalStatuses = new Set<string>(DOCUMENT_JOB_END_STATUSES)

/** In-flight attempt breadcrumbs (recordOcrAttempt / the sync pipeline's own onAttempt) — same-status events, no real transition. */
const attemptPhaseLabels: Record<string, string> = {
    started: 'started',
    succeeded: 'succeeded',
    'quality-rejected': 'quality rejected',
    failed: 'failed',
    retrying: 'retrying',
    skipped: 'skipped',
}
const attemptPhaseTones: Record<string, Tone> = {
    started: 'neutral',
    succeeded: 'success',
    'quality-rejected': 'warning',
    failed: 'danger',
    retrying: 'warning',
    skipped: 'neutral',
}

/** One real event or in-flight breadcrumb, chronological, nested under its owning `PipelineNode`. */
interface NodeAttempt {
    key: string
    label: string
    tone: Tone
    at: string
    actor: string | null
    detail: Record<string, unknown> | null
    isBreadcrumb: boolean
}

type NodeStatus = 'succeeded' | 'failed' | 'skipped' | 'in-progress' | 'not-reached'

const nodeStatusLabels: Record<NodeStatus, string> = {
    succeeded: 'succeeded',
    failed: 'failed',
    skipped: 'skipped',
    'in-progress': 'in progress',
    'not-reached': 'not reached',
}
const nodeStatusTones: Record<NodeStatus, Tone> = {
    succeeded: 'success',
    failed: 'danger',
    skipped: 'neutral',
    'in-progress': 'primary',
    'not-reached': 'neutral',
}

/**
 * One logical pipeline NODE — this is the audit unit that actually matters: which
 * service/process executed it (`executor`), when it picked up work and when it
 * returned, whether it ran at all (`status` distinguishes 'skipped' — deliberately
 * bypassed, e.g. visual mode or a cache hit — from 'not-reached' — the job never
 * got this far, usually because an earlier node failed or the job is still
 * in-flight), and every real attempt underneath it (`attempts` — e.g. the OCR
 * the OCR readers tried one by one, or the batch submit/poll/reconcile sub-phases).
 *
 * Grouped by logical role, not by raw `step` value 1:1 — the "Extract" node spans
 * four different step tags (`extract` for the sync path; `batch_submit` /
 * `batch_poll` / `reconcile` for the batch path) because they're the same node
 * from an audit standpoint: "the LLM extraction step," just with two different
 * dispatch mechanisms underneath. See `NODE_GROUPS` below.
 */
interface PipelineNode {
    key: string
    name: string
    status: NodeStatus
    /** Why 'skipped', when that's the status — always shown, never left to speak for itself. */
    skipReason?: string
    /** The service/worker/process that actually executed this node — a document-worker id
     * (`cloud:<execution>` or a device), 'sync-pipeline', 'batcher', etc. Null if the node
     * never ran (nothing to attribute execution to). */
    executor: string | null
    startedAt: string | null
    endedAt: string | null
    durationMs: number | null
    attempts: NodeAttempt[]
}

function toNodeAttempt(event: DocumentJobEvent): NodeAttempt {
    const step = event.step ?? DocumentProcessingStep.UNKNOWN
    const isBreadcrumb = event.from_status === event.to_status
    const phase = event.detail?.phase as string | undefined

    let tone: Tone = 'neutral'
    if (phase && attemptPhaseTones[phase]) tone = attemptPhaseTones[phase]
    else if (failureToStatuses.has(event.to_status) && !isBreadcrumb) tone = 'danger'
    else if (!isBreadcrumb) tone = 'primary'

    const baseLabel = stepLabels[step] || step
    const attemptNumber = event.detail?.attempt
    const adapter = event.detail?.adapter as string | undefined
    let label = isBreadcrumb
        ? `${baseLabel} — ${attemptPhaseLabels[phase ?? ''] ?? phase ?? 'update'}`
        : `${baseLabel}: ${event.from_status ?? '(new)'} → ${event.to_status}`
    if (adapter) label += ` (${adapter}${attemptNumber ? `, attempt ${attemptNumber}` : ''})`

    return { key: String(event.id), label, tone, at: event.at, actor: event.actor, detail: event.detail, isBreadcrumb }
}

/** Which raw `step` tags roll up into which logical node — see `PipelineNode`'s docstring for why Extract spans four. */
const NODE_GROUPS: { key: string; name: string; steps: string[] }[] = [
    { key: 'enqueue', name: 'Enqueue', steps: [DocumentProcessingStep.ENQUEUE] },
    { key: 'dedup_lookup', name: 'Dedup Lookup', steps: [DocumentProcessingStep.DEDUP_LOOKUP] },
    { key: 'ocr', name: 'OCR', steps: [DocumentProcessingStep.OCR] },
    {
        key: 'extract',
        name: 'Extract',
        steps: [DocumentProcessingStep.EXTRACT, DocumentProcessingStep.BATCH_SUBMIT, DocumentProcessingStep.BATCH_POLL, DocumentProcessingStep.RECONCILE],
    },
    { key: 'persist', name: 'Persist', steps: [DocumentProcessingStep.PERSIST] },
]

/**
 * Groups the raw event log into logical pipeline nodes (see `PipelineNode`). This is the
 * whole point of the redesign: an auditor asks "did the OCR node run, who ran it, how long
 * did it take, did it retry, was it skipped" — none of which a flat chronological event list
 * answers directly. Grouping by node answers all of them per-node, with the raw events still
 * available underneath as `attempts` for anyone who wants the ground truth.
 */
function buildPipelineNodes(events: DocumentJobEvent[], job: DocumentJobRow): PipelineNode[] {
    const sorted = [...events].sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime())
    const isTerminal = terminalStatuses.has(job.status)

    const dedupEvents = sorted.filter((e) => e.step === DocumentProcessingStep.DEDUP_LOOKUP)
    // A cache hit is a legitimate reason for OCR/Extract to never run at all — distinct from
    // "not reached because something earlier failed" or "still in flight."
    const isCacheHit =
        dedupEvents.length > 0 &&
        !sorted.some((e) =>
            [DocumentProcessingStep.OCR, DocumentProcessingStep.EXTRACT, DocumentProcessingStep.BATCH_SUBMIT, DocumentProcessingStep.RECONCILE].includes(
                (e.step ?? '') as DocumentProcessingStep
            )
        )

    const nodes: PipelineNode[] = []

    for (const group of NODE_GROUPS) {
        if (group.key === 'dedup_lookup' && dedupEvents.length === 0) continue // only ever shown when it actually ran

        const groupEvents = sorted.filter((e) => group.steps.includes(e.step ?? DocumentProcessingStep.UNKNOWN))
        const attempts = groupEvents.map(toNodeAttempt)

        const skipBreadcrumb = groupEvents.find((e) => e.detail?.phase === 'skipped')

        let status: NodeStatus
        let skipReason: string | undefined

        if (skipBreadcrumb) {
            status = 'skipped'
            skipReason = (skipBreadcrumb.detail?.reason as string) ?? 'skipped'
        } else if (groupEvents.length === 0) {
            if (isCacheHit && (group.key === 'ocr' || group.key === 'extract')) {
                status = 'skipped'
                skipReason = 'served from the global content-hash cache — see Dedup Lookup'
            } else {
                // No events at all: either the job never got this far (still running, or an
                // earlier node ended it), or this node's step genuinely never applies here.
                status = 'not-reached'
            }
        } else {
            // A real (non-breadcrumb) transition landing on a failed status, inside this node's own
            // events, means THIS node is what failed the job.
            const nodeFailedHere = groupEvents.some((e) => failedStatuses.has(e.to_status) && e.from_status !== e.to_status)
            if (nodeFailedHere) status = 'failed'
            else if (!isTerminal && group.key === NODE_GROUPS.filter((g) => g.key !== 'dedup_lookup' || dedupEvents.length).slice(-1)[0]?.key)
                status = 'in-progress'
            else status = 'succeeded'
        }

        const executor = [...groupEvents].reverse().find((e) => e.actor)?.actor ?? null
        const startedAt = groupEvents[0]?.at ?? null
        const endedAt = groupEvents.length ? groupEvents[groupEvents.length - 1].at : null

        nodes.push({ key: group.key, name: group.name, status, skipReason, executor, startedAt, endedAt, durationMs: null, attempts })
    }

    // The Extract node's true end is when its output actually got written — that's the
    // Persist node's own timestamp, not "reconcile started" (extraction is still running at
    // that point) or "batch_submit succeeded" (that's the START of a wait, not the end).
    const persistNode = nodes.find((n) => n.key === 'persist')
    const extractNode = nodes.find((n) => n.key === 'extract')
    if (extractNode && persistNode?.startedAt && extractNode.startedAt && extractNode.status !== 'skipped' && extractNode.status !== 'not-reached') {
        extractNode.endedAt = persistNode.startedAt
    }

    for (const node of nodes) {
        node.durationMs = node.startedAt && node.endedAt ? new Date(node.endedAt).getTime() - new Date(node.startedAt).getTime() : null
    }

    return nodes
}

/** Tailwind badge classes per tone — light/dark pairs, kept in one place so every status pill stays visually consistent. */
const toneBadgeClasses: Record<Tone, string> = {
    success: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-400 dark:border-emerald-500/30',
    danger: 'bg-red-50 text-red-700 border-red-200 dark:bg-red-500/10 dark:text-red-400 dark:border-red-500/30',
    warning: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-500/10 dark:text-amber-400 dark:border-amber-500/30',
    primary: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-500/10 dark:text-blue-400 dark:border-blue-500/30',
    neutral: 'bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700',
}

const toneDotClasses: Record<Tone, string> = {
    success: 'text-emerald-500',
    danger: 'text-red-500',
    warning: 'text-amber-500',
    primary: 'text-blue-500',
    neutral: 'text-slate-400 dark:text-slate-500',
}

function ToneIcon({ tone, className }: { tone: Tone; className?: string }) {
    if (tone === 'success') return <CheckCircleRoundedIcon className={className} />
    if (tone === 'danger') return <CancelRoundedIcon className={className} />
    if (tone === 'primary') return <ArrowRightAltRoundedIcon className={className} />
    return <FiberManualRecordRoundedIcon className={className} />
}

/** A dash for 'not reached' — distinct from every real status icon, since nothing happened. */
function NodeStatusIcon({ status, className }: { status: NodeStatus; className?: string }) {
    if (status === 'not-reached') return <span className={className}>—</span>
    return <ToneIcon tone={nodeStatusTones[status]} className={className} />
}

export default function DocumentJobsPage() {
    const { mode: colorMode } = useColorScheme()
    const isDarkMode = colorMode === 'dark'
    const [rows, setRows] = React.useState<DocumentJobListRow[]>([])
    const [page, setPage] = React.useState(1)
    const [loading, setLoading] = React.useState(true)
    const [fetching, setFetching] = React.useState(false)
    const [totalCount, setTotalCount] = React.useState(0)
    const [totalPages, setTotalPages] = React.useState(0)

    // Drill-downs from the Pipeline Audit arrive with their filters in the URL.
    const [searchParams] = useSearchParams()
    const initial = (key: string) => searchParams.get(key) ?? ''
    const [projectFilter, setProjectFilter] = React.useState(initial('projectName'))
    const [statusFilter, setStatusFilter] = React.useState(initial('status'))
    const [modeFilter, setModeFilter] = React.useState('')
    const [producerFilter, setProducerFilter] = React.useState(initial('producer'))
    const [outcomeFilter, setOutcomeFilter] = React.useState(initial('outcome'))
    const [docTypeHintFilter, setDocTypeHintFilter] = React.useState('')

    const [appliedProjectFilter, setAppliedProjectFilter] = React.useState(initial('projectName'))
    const [appliedStatusFilter, setAppliedStatusFilter] = React.useState(initial('status'))
    const [appliedModeFilter, setAppliedModeFilter] = React.useState('')
    const [appliedProducerFilter, setAppliedProducerFilter] = React.useState(initial('producer'))
    const [appliedOutcomeFilter, setAppliedOutcomeFilter] = React.useState(initial('outcome'))
    /** From a drill-down only: the audit window's start. Cleared with the other filters. */
    const [appliedSince, setAppliedSince] = React.useState(initial('startDate'))
    /** From a drill-down on one day: the end of that day. Cleared together with `appliedSince`. */
    const [appliedUntil, setAppliedUntil] = React.useState(initial('endDate'))
    /** Set by clicking a batch chip: show only that batch's jobs. Cleared with the other filters. */
    const [appliedBatchId, setAppliedBatchId] = React.useState(initial('batchId'))
    const [appliedDocTypeHintFilter, setAppliedDocTypeHintFilter] = React.useState('')

    // Bumped by the Refresh button so the fetch effect below always re-runs, even
    // when page/filters haven't changed — setPage(1) alone is a no-op when page is
    // already 1 (React bails out on an unchanged value), which is the common case.
    const [refreshTick, setRefreshTick] = React.useState(0)

    const [selectedJob, setSelectedJob] = React.useState<DocumentJobRow | null>(null)
    const [detailModalOpen, setDetailModalOpen] = React.useState(false)
    const [eventsLoading, setEventsLoading] = React.useState(false)
    const [events, setEvents] = React.useState<DocumentJobEvent[] | null>(null)
    const [eventsError, setEventsError] = React.useState<string | null>(null)
    // Every pipeline step collapses by default so the whole timeline is scannable without
    // scrolling — a step's key (its event id) lives here only while expanded.
    const [expandedStages, setExpandedStages] = React.useState<Set<string>>(new Set())
    const toggleStage = (key: string) =>
        setExpandedStages((prev) => {
            const next = new Set(prev)
            if (next.has(key)) next.delete(key)
            else next.add(key)
            return next
        })

    const [detail, setDetail] = React.useState<DocumentJobDetailResponse | null>(null)
    const [detailLoading, setDetailLoading] = React.useState(false)
    const [detailError, setDetailError] = React.useState<string | null>(null)

    const handleFilterKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            event.preventDefault()
            applyFilters()
        }
    }

    const applyFilters = () => {
        setPage(1)
        setAppliedProjectFilter(projectFilter)
        setAppliedStatusFilter(statusFilter)
        setAppliedModeFilter(modeFilter)
        setAppliedProducerFilter(producerFilter)
        setAppliedOutcomeFilter(outcomeFilter)
        setAppliedDocTypeHintFilter(docTypeHintFilter)
    }

    const clearFilters = () => {
        setProjectFilter('')
        setStatusFilter('')
        setModeFilter('')
        setProducerFilter('')
        setAppliedProducerFilter('')
        setOutcomeFilter('')
        setAppliedOutcomeFilter('')
        setAppliedSince('')
        setAppliedUntil('')
        setAppliedBatchId('')
        setDocTypeHintFilter('')
        setAppliedProjectFilter('')
        setAppliedStatusFilter('')
        setAppliedModeFilter('')
        setAppliedDocTypeHintFilter('')
        setPage(1)
    }

    const [reprocessing, setReprocessing] = React.useState(false)
    const [reprocessError, setReprocessError] = React.useState<string | null>(null)

    /** An ended job goes back to ocr_queued: it is read again and its result handler runs again (ADR-022 Lifecycle). */
    const reprocessJob = async (job: DocumentJobRow) => {
        setReprocessing(true)
        setReprocessError(null)
        try {
            await axios.post(`/gluon/document-jobs/${job.id}/reprocess/v1`)
            setDetailModalOpen(false)
            setRefreshTick((t) => t + 1)
        } catch (error) {
            setReprocessError((error as { response?: { data?: { message?: string } } }).response?.data?.message ?? 'Failed to reprocess the job')
        } finally {
            setReprocessing(false)
        }
    }

    const openDetailModal = (job: DocumentJobRow) => {
        setReprocessError(null)
        setSelectedJob(job)
        setEvents(null)
        setEventsError(null)
        setDetail(null)
        setDetailError(null)
        setExpandedStages(new Set())
        setDetailModalOpen(true)
        loadEvents(job.id)
        loadDetail(job.id)
    }

    const loadEvents = async (jobId: string) => {
        setEventsLoading(true)
        setEventsError(null)
        try {
            const res = await axios.get(`/gluon/document-jobs/${jobId}/events/v1`)
            setEvents(res.data?.data?.events ?? [])
        } catch {
            setEventsError('Failed to load the job timeline')
        } finally {
            setEventsLoading(false)
        }
    }

    /** Fetches the source-document preview URL, OCR text, and LLM extraction output for the audit view below. */
    const loadDetail = async (jobId: string) => {
        setDetailLoading(true)
        setDetailError(null)
        try {
            const res = await axios.get(`/gluon/document-jobs/${jobId}/v1`)
            setDetail(res.data?.data ?? null)
        } catch {
            setDetailError('Failed to load the document, OCR output, and extraction output')
        } finally {
            setDetailLoading(false)
        }
    }

    React.useEffect(() => {
        const fetchData = async () => {
            // refreshTick === 0 excludes a Refresh-button click from counting as the initial
            // load — otherwise a refresh on page 1 with no filters would show the full loading
            // skeleton (blanking the table) instead of the lighter `fetching` spinner.
            const isInitialLoad =
                refreshTick === 0 &&
                page === 1 &&
                !appliedProjectFilter &&
                !appliedStatusFilter &&
                !appliedModeFilter &&
                !appliedProducerFilter &&
                !appliedOutcomeFilter &&
                !appliedSince &&
                !appliedUntil &&
                !appliedBatchId &&
                !appliedDocTypeHintFilter
            if (isInitialLoad) setLoading(true)
            else setFetching(true)

            try {
                const params = new URLSearchParams()
                params.append('page', page.toString())
                params.append('pageSize', PAGE_SIZE.toString())
                if (appliedProjectFilter) params.append('projectName', appliedProjectFilter)
                if (appliedStatusFilter) params.append('status', appliedStatusFilter)
                if (appliedModeFilter) params.append('mode', appliedModeFilter)
                if (appliedProducerFilter) params.append('producer', appliedProducerFilter)
                if (appliedOutcomeFilter) params.append('outcome', appliedOutcomeFilter)
                if (appliedSince) params.append('startDate', appliedSince)
                if (appliedUntil) params.append('endDate', appliedUntil)
                if (appliedBatchId) params.append('batchId', appliedBatchId)
                if (appliedDocTypeHintFilter) params.append('docTypeHint', appliedDocTypeHintFilter)

                const res = await axios.get(`/gluon/document-jobs/v1?${params.toString()}`)
                const data = res.data?.data ?? {}

                setRows(Array.isArray(data.rows) ? data.rows : [])
                setTotalCount(typeof data.total === 'number' ? data.total : 0)
                setTotalPages(typeof data.totalPages === 'number' ? data.totalPages : 0)
            } catch (e) {
                console.error('Failed to fetch document jobs:', e)
                setRows([])
                setTotalCount(0)
                setTotalPages(0)
            } finally {
                setLoading(false)
                setFetching(false)
            }
        }

        fetchData()
    }, [
        page,
        appliedProjectFilter,
        appliedStatusFilter,
        appliedModeFilter,
        appliedProducerFilter,
        appliedOutcomeFilter,
        appliedSince,
        appliedUntil,
        appliedBatchId,
        appliedDocTypeHintFilter,
        refreshTick,
    ])

    React.useEffect(() => {
        if (totalPages > 0 && page > totalPages) setPage(1)
    }, [totalPages, page])

    const pipelineNodes = React.useMemo(() => (selectedJob ? buildPipelineNodes(events ?? [], selectedJob) : []), [events, selectedJob])

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
                <Typography sx={{ ml: 2 }}>Loading document jobs...</Typography>
            </Box>
        )
    }

    return (
        <React.Fragment>
            <Box
                sx={{
                    display: 'flex',
                    my: 2,
                    gap: 1,
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'start', sm: 'center' },
                    flexWrap: 'wrap',
                    justifyContent: 'space-between',
                }}
            >
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Typography level="h2" component="h1">
                        Document Jobs ({totalCount})
                    </Typography>
                    {fetching && <CircularProgress size="sm" />}
                </Box>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Button size="sm" variant="outlined" startDecorator={<RefreshIcon />} disabled={fetching} onClick={() => setRefreshTick((t) => t + 1)}>
                        Refresh
                    </Button>
                    <Button size="sm" variant="outlined" disabled={page === 1 || fetching} onClick={() => setPage(page - 1)}>
                        Previous
                    </Button>
                    <Typography level="body-sm">
                        Page {page} of {totalPages || 1}
                    </Typography>
                    <Button size="sm" variant="outlined" disabled={page >= totalPages || fetching} onClick={() => setPage(page + 1)}>
                        Next
                    </Button>
                </Box>
            </Box>

            <Box sx={{ borderRadius: 'sm', py: 2, display: { xs: 'none', sm: 'flex' }, flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, width: '100%', alignItems: 'flex-end' }}>
                    <FormControl sx={{ flex: 1 }} size="sm">
                        <FormLabel>Project Name</FormLabel>
                        <Input
                            size="sm"
                            placeholder="Exact match..."
                            startDecorator={<SearchIcon />}
                            value={projectFilter}
                            onChange={(e) => setProjectFilter(e.target.value)}
                            onKeyDown={handleFilterKeyDown}
                        />
                    </FormControl>
                    <FormControl sx={{ flex: 1 }} size="sm">
                        <FormLabel>Doc Type Hint</FormLabel>
                        <Input
                            size="sm"
                            placeholder="Exact match..."
                            value={docTypeHintFilter}
                            onChange={(e) => setDocTypeHintFilter(e.target.value)}
                            onKeyDown={handleFilterKeyDown}
                        />
                    </FormControl>
                    <FormControl sx={{ flex: 1 }} size="sm">
                        <FormLabel>Status</FormLabel>
                        <Select size="sm" placeholder="All statuses" value={statusFilter} onChange={(_, val) => setStatusFilter(val || '')}>
                            <Option value="">All</Option>
                            <Option value={FAILED_STATUS_FILTER}>failed, OCR or LLM</Option>
                            {Object.values(DocumentJobStatus).map((status) => (
                                <Option key={status} value={status}>
                                    {status}
                                </Option>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl sx={{ flex: 1 }} size="sm">
                        <FormLabel>Producer</FormLabel>
                        <Select size="sm" placeholder="All producers" value={producerFilter} onChange={(_, val) => setProducerFilter(val || '')}>
                            <Option value="">All</Option>
                            {Object.entries(PIPELINE_PRODUCER_LABELS).map(([value, label]) => (
                                <Option key={value} value={value}>
                                    {label}
                                </Option>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl sx={{ flex: 1 }} size="sm">
                        <FormLabel>Outcome</FormLabel>
                        <Select size="sm" placeholder="Any outcome" value={outcomeFilter} onChange={(_, val) => setOutcomeFilter(val || '')}>
                            <Option value="">Any</Option>
                            {Object.entries(OUTCOME_LABELS).map(([value, label]) => (
                                <Option key={value} value={value}>
                                    {label}
                                </Option>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl sx={{ flex: 1 }} size="sm">
                        <FormLabel>Mode</FormLabel>
                        <Select size="sm" placeholder="All modes" value={modeFilter} onChange={(_, val) => setModeFilter(val || '')}>
                            <Option value="">All</Option>
                            {Object.values(DocumentMode).map((mode) => (
                                <Option key={mode} value={mode}>
                                    {mode}
                                </Option>
                            ))}
                        </Select>
                    </FormControl>
                    {appliedBatchId && (
                        <Chip
                            size="sm"
                            variant="soft"
                            color="primary"
                            sx={{ alignSelf: 'flex-end', mb: 0.5, fontFamily: 'monospace' }}
                            endDecorator={<CloseRoundedIcon fontSize="small" sx={{ cursor: 'pointer' }} onClick={() => setAppliedBatchId('')} />}
                        >
                            batch {appliedBatchId.slice(0, 6)}
                        </Chip>
                    )}
                    {(appliedSince || appliedUntil) && (
                        <Chip
                            size="sm"
                            variant="soft"
                            color="primary"
                            sx={{ alignSelf: 'flex-end', mb: 0.5 }}
                            endDecorator={
                                <CloseRoundedIcon
                                    fontSize="small"
                                    sx={{ cursor: 'pointer' }}
                                    onClick={() => {
                                        setAppliedSince('')
                                        setAppliedUntil('')
                                    }}
                                />
                            }
                        >
                            {enqueuedRangeLabel(appliedSince, appliedUntil)}
                        </Chip>
                    )}
                    <Box sx={{ display: 'flex', gap: 1, alignSelf: 'flex-end' }}>
                        <Button size="sm" variant="solid" color="primary" onClick={applyFilters}>
                            Apply
                        </Button>
                        <Button size="sm" variant="outlined" color="neutral" onClick={clearFilters}>
                            Clear
                        </Button>
                    </Box>
                </Box>
            </Box>

            <Sheet
                variant="outlined"
                sx={{ display: { xs: 'none', sm: 'initial' }, width: '100%', borderRadius: 'sm', flexShrink: 1, overflow: 'auto', minHeight: 0 }}
            >
                <Table
                    aria-labelledby="document-jobs-table-title"
                    stickyHeader
                    hoverRow
                    sx={{
                        '--TableCell-headBackground': 'var(--joy-palette-background-level1)',
                        '--Table-headerUnderlineThickness': '1px',
                        '--TableRow-hoverBackground': 'var(--joy-palette-background-level1)',
                        '--TableCell-paddingY': '4px',
                        '--TableCell-paddingX': '8px',
                    }}
                >
                    <thead>
                        <tr>
                            <th style={{ width: 50, padding: '12px 6px' }}>Actions</th>
                            <th style={{ width: 150, padding: '12px 6px' }}>Project</th>
                            <th style={{ width: 120, padding: '12px 6px' }}>Doc Type Hint</th>
                            <th style={{ width: 80, padding: '12px 6px' }}>Mode</th>
                            <th style={{ width: 130, padding: '12px 6px' }}>Status</th>
                            <th style={{ width: 150, padding: '12px 6px' }}>Result</th>
                            <th style={{ width: 230, padding: '12px 6px' }}>
                                <Tooltip
                                    title="The Vertex batch that extracted this job. Rows with the same color were submitted together; a follower (↳) reused its leader's result from that batch. Shows the batch's model, how many files it sent, and its input → output tokens."
                                    arrow
                                >
                                    <span style={{ cursor: 'help', borderBottom: '1px dotted' }}>Batch</span>
                                </Tooltip>
                            </th>
                            <th style={{ width: 120, padding: '12px 6px' }}>
                                <Tooltip
                                    title="Input → output tokens of this job's own model calls: the classifier, its batch row and any synchronous retry. Output includes thinking. A follower makes no calls; it reuses its leader's result."
                                    arrow
                                >
                                    <span style={{ cursor: 'help', borderBottom: '1px dotted' }}>Job tokens</span>
                                </Tooltip>
                            </th>
                            <th style={{ width: 200, padding: '12px 6px' }}>Content Hash</th>
                            <th style={{ width: 160, padding: '12px 6px' }}>Enqueued At</th>
                            <th style={{ width: 160, padding: '12px 6px' }}>Updated At</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row) => (
                            <tr key={row.id}>
                                <td>
                                    <Tooltip title="View Details">
                                        <IconButton size="sm" variant="plain" onClick={() => openDetailModal(row)}>
                                            <VisibilityIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </td>
                                <td>
                                    <Tooltip title={row.project_name}>
                                        <Typography level="body-xs" sx={{ cursor: 'default' }}>
                                            {truncateText(row.project_name, 20)}
                                        </Typography>
                                    </Tooltip>
                                </td>
                                <td>
                                    <Typography level="body-xs">{row.doc_type_hint || 'N/A'}</Typography>
                                </td>
                                <td>
                                    <Typography level="body-xs">{row.mode}</Typography>
                                </td>
                                <td>
                                    <Tooltip title={STATUS_HINTS[row.status] ?? ''} disableHoverListener={!STATUS_HINTS[row.status]}>
                                        <Chip size="sm" variant="soft" color={statusColors[row.status] || 'neutral'}>
                                            {row.status}
                                        </Chip>
                                    </Tooltip>
                                </td>
                                <td>
                                    <Typography level="body-xs" sx={{ fontFamily: 'monospace' }}>
                                        {row.result_ref ?? '—'}
                                    </Typography>
                                </td>
                                <td>
                                    <BatchCell
                                        row={row}
                                        onSelect={(batchId) => {
                                            setPage(1)
                                            setAppliedBatchId(batchId)
                                        }}
                                    />
                                </td>
                                <td>
                                    <Tooltip
                                        title={`${Number(row.llm_input_tokens).toLocaleString()} input · ${Number(row.llm_output_tokens).toLocaleString()} output tokens`}
                                    >
                                        <Typography level="body-xs" sx={{ cursor: 'default', fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                                            {row.leader_job_id && Number(row.llm_input_tokens) === 0
                                                ? 'reused'
                                                : `${formatTokens(row.llm_input_tokens)} → ${formatTokens(row.llm_output_tokens)}`}
                                        </Typography>
                                    </Tooltip>
                                </td>
                                <td>
                                    <Tooltip title={row.content_hash}>
                                        <Typography level="body-xs" sx={{ cursor: 'default', fontFamily: 'monospace' }}>
                                            {truncateText(row.content_hash, 18)}
                                        </Typography>
                                    </Tooltip>
                                </td>
                                <td>
                                    <Typography level="body-xs">{row.enqueued_at ? formatToUserLocalTime(row.enqueued_at) : 'N/A'}</Typography>
                                </td>
                                <td>
                                    <Typography level="body-xs">{row.updated_at ? formatToUserLocalTime(row.updated_at) : 'N/A'}</Typography>
                                </td>
                            </tr>
                        ))}
                        {rows.length === 0 && (
                            <tr>
                                <td colSpan={11} style={{ textAlign: 'center', padding: '32px' }}>
                                    <Typography level="body-sm" color="neutral">
                                        No document jobs found
                                    </Typography>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </Table>
            </Sheet>

            <Modal open={detailModalOpen} onClose={() => setDetailModalOpen(false)}>
                <ModalDialog
                    variant="plain"
                    sx={{ p: 0, bgcolor: 'transparent', boxShadow: 'none', border: 'none', width: 'min(1080px, 96vw)', height: 'min(720px, 90vh)' }}
                >
                    <div
                        className={`flex h-full w-full flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl dark:border-slate-700 dark:bg-slate-900 ${isDarkMode ? 'dark' : ''}`}
                    >
                        {/* Header */}
                        <div className="flex shrink-0 items-center justify-between border-b border-slate-200 px-4 py-3 dark:border-slate-700">
                            <div className="flex items-center gap-2.5">
                                <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">Document Job Audit</span>
                                {selectedJob && (
                                    <span
                                        className={`rounded-full border px-2 py-0.5 text-[11px] font-medium ${toneBadgeClasses[statusColors[selectedJob.status] || 'neutral']}`}
                                    >
                                        {selectedJob.status}
                                    </span>
                                )}
                            </div>
                            <button
                                type="button"
                                onClick={() => setDetailModalOpen(false)}
                                className="rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800 dark:hover:text-slate-300"
                                aria-label="Close"
                            >
                                <CloseRoundedIcon sx={{ fontSize: 18 }} />
                            </button>
                        </div>

                        {selectedJob && (
                            <>
                                {/* Identity strip — every field is a tooltip: this bar is dense on purpose, the
                                    explanation is one hover away rather than always on screen. */}
                                <div className="flex shrink-0 flex-wrap gap-x-4 gap-y-1 border-b border-slate-200 px-4 py-2 text-xs text-slate-500 dark:border-slate-700 dark:text-slate-400">
                                    <Tooltip
                                        title="Internal document_jobs.id — the unique identifier for this one processing attempt. Resubmitting the exact same file creates a new job id even though the content hash below stays identical."
                                        arrow
                                    >
                                        <span className="cursor-help border-b border-dotted border-slate-300 dark:border-slate-600">
                                            job <span className="font-mono text-slate-600 dark:text-slate-300">{truncateText(selectedJob.id, 12)}</span>
                                        </span>
                                    </Tooltip>
                                    <Tooltip
                                        title="SHA-256 of the source file's raw bytes. Content-addressed: any job, from any tenant, with this same hash reuses one shared OCR/extraction result instead of reprocessing — that's what a sync-mode 'cached: true' result means."
                                        arrow
                                    >
                                        <span className="cursor-help border-b border-dotted border-slate-300 dark:border-slate-600">
                                            hash{' '}
                                            <span className="font-mono text-slate-600 dark:text-slate-300">{truncateText(selectedJob.content_hash, 16)}</span>
                                        </span>
                                    </Tooltip>
                                    <Tooltip
                                        title="The tenant/project this request was submitted under. For a manual run via Document Processing Test, this is just whatever name the tester typed in — it has no other significance."
                                        arrow
                                    >
                                        <span className="cursor-help border-b border-dotted border-slate-300 dark:border-slate-600">
                                            project {selectedJob.project_name}
                                        </span>
                                    </Tooltip>
                                    <Tooltip
                                        title={`source: how this document entered the pipeline. mode '${selectedJob.mode}': ${selectedJob.mode === 'immediate' ? "synchronous — the caller's HTTP request blocked until this job was fully done" : 'batch — queued as ocr_queued and processed later by the cloud worker (OCR drain hourly, LLM node every 5 minutes)'}. extract_mode '${selectedJob.extract_mode}': ${selectedJob.extract_mode === 'visual' ? 'no OCR reader could read it (or OCR was skipped) — the LLM read the source document/image directly' : 'the LLM read OCR-extracted text, not the raw document — see Extraction Artifacts above'}.`}
                                        arrow
                                    >
                                        <span className="cursor-help border-b border-dotted border-slate-300 dark:border-slate-600">
                                            {selectedJob.source} · {selectedJob.mode} / {selectedJob.extract_mode}
                                        </span>
                                    </Tooltip>
                                    <Tooltip
                                        title="The submitter's UNVERIFIED guess at the document type — purely a hint the pipeline may ignore. The pipeline always re-derives the real type itself; see 'resolved' (once terminal) or Extraction Output above for what it actually decided."
                                        arrow
                                    >
                                        <span className="cursor-help border-b border-dotted border-slate-300 dark:border-slate-600">
                                            hint: {selectedJob.doc_type_hint || 'none'}
                                        </span>
                                    </Tooltip>
                                    {selectedJob.resolved_doc_type && (
                                        <Tooltip
                                            title="The doc type the pipeline actually settled on, once the job reached a terminal state — this is the ground truth, not the submitter's hint above. 'not_of_interest' means it was correctly classified as a type this pipeline doesn't handle — a designed success, not a failure. 'combined' means the file holds more than one document, so nothing was extracted from it."
                                            arrow
                                        >
                                            <span className="cursor-help border-b border-dotted border-slate-300 dark:border-slate-600">
                                                resolved: {selectedJob.resolved_doc_type}
                                            </span>
                                        </Tooltip>
                                    )}
                                    <Tooltip
                                        title="Input → output tokens of this job's own model calls: the classifier, its batch row and any synchronous retry. Output includes thinking."
                                        arrow
                                    >
                                        <span className="cursor-help border-b border-dotted border-slate-300 dark:border-slate-600">
                                            tokens {Number(selectedJob.llm_input_tokens).toLocaleString()} →{' '}
                                            {Number(selectedJob.llm_output_tokens).toLocaleString()}
                                        </span>
                                    </Tooltip>
                                    {selectedJob.batch_id && (
                                        <Tooltip
                                            title="Which Vertex Batch Prediction submission this job was grouped into. Every job submitted in the same drainer pass shares one batch_id and is reconciled together when that batch's results come back."
                                            arrow
                                        >
                                            <span className="cursor-help border-b border-dotted border-slate-300 dark:border-slate-600">
                                                batch{' '}
                                                <span className="font-mono text-slate-600 dark:text-slate-300">{truncateText(selectedJob.batch_id, 14)}</span>
                                            </span>
                                        </Tooltip>
                                    )}
                                    <Tooltip
                                        title="Attempts used per stage, out of 3 each. OCR counts at each lease (an expired lease or a transient OCR failure uses one); extract counts at each batch claim. A stage that reaches 3 fails the job."
                                        arrow
                                    >
                                        <span className="cursor-help border-b border-dotted border-slate-300 dark:border-slate-600">
                                            attempts ocr {selectedJob.ocr_attempts} · extract {selectedJob.extract_attempts}
                                        </span>
                                    </Tooltip>
                                    {terminalStatuses.has(selectedJob.status) && (
                                        <Tooltip
                                            title="Send this ended job back to ocr_queued: it is read again from the start and its result handler runs again. The stored result is replaced when the new one is ready."
                                            arrow
                                        >
                                            <Button
                                                size="sm"
                                                variant="outlined"
                                                color="neutral"
                                                loading={reprocessing}
                                                onClick={() => reprocessJob(selectedJob)}
                                            >
                                                Reprocess
                                            </Button>
                                        </Tooltip>
                                    )}
                                    {reprocessError && <span className="text-red-600 dark:text-red-400">{reprocessError}</span>}
                                    <span className="ml-auto whitespace-nowrap">
                                        enqueued {formatToUserLocalTime(selectedJob.enqueued_at)} · updated {formatToUserLocalTime(selectedJob.updated_at)}
                                    </span>
                                </div>

                                {selectedJob.last_error && (
                                    <div className="mx-4 mt-3 shrink-0 rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-500/30 dark:bg-red-500/10">
                                        <p className="text-xs font-semibold text-red-700 dark:text-red-400">
                                            Last error ({selectedJob.last_step || 'unknown step'})
                                        </p>
                                        <p className="mt-0.5 whitespace-pre-wrap text-xs text-red-700 dark:text-red-300">{selectedJob.last_error}</p>
                                    </div>
                                )}

                                {/* Body — stacked on small viewports so nothing gets clipped, side-by-side from md up */}
                                <div className="flex min-h-0 flex-1 flex-col overflow-y-auto md:grid md:grid-cols-[260px_1fr] md:overflow-hidden">
                                    {/* Left rail — the one constant input across the whole pipeline */}
                                    <div className="shrink-0 border-b border-slate-200 p-4 dark:border-slate-700 md:h-full md:overflow-y-auto md:border-b-0 md:border-r">
                                        <p className="mb-2 text-xs font-semibold text-slate-700 dark:text-slate-300">Source Document</p>
                                        {detailLoading && <CircularProgress size="sm" />}
                                        {detailError && (
                                            <div>
                                                <p className="mb-2 text-xs text-amber-600 dark:text-amber-400">{detailError}</p>
                                                <button
                                                    type="button"
                                                    onClick={() => loadDetail(selectedJob.id)}
                                                    className="rounded-lg border border-slate-300 px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-800"
                                                >
                                                    Retry
                                                </button>
                                            </div>
                                        )}
                                        {detail && (
                                            <>
                                                {detail.previewUrl ? (
                                                    <div className="mb-2 aspect-[3/4] overflow-hidden rounded-lg border border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800">
                                                        {isImagePath(selectedJob.filename || selectedJob.blob_uri) ? (
                                                            <img
                                                                src={detail.previewUrl}
                                                                alt={selectedJob.filename || 'document preview'}
                                                                className="h-full w-full object-contain"
                                                            />
                                                        ) : (
                                                            <iframe
                                                                src={`${detail.previewUrl}#toolbar=0`}
                                                                title="Document preview"
                                                                className="h-full w-full border-none"
                                                            />
                                                        )}
                                                    </div>
                                                ) : (
                                                    <div className="mb-2 flex aspect-[3/4] flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-slate-200 bg-slate-50 p-3 text-center dark:border-slate-700 dark:bg-slate-800">
                                                        <InsertDriveFileOutlinedIcon sx={{ fontSize: 28 }} className="text-slate-300 dark:text-slate-600" />
                                                        <p className="text-[11px] text-slate-400 dark:text-slate-500">
                                                            Preview unavailable — the signed URL could not be generated (the source object may no longer exist).
                                                        </p>
                                                    </div>
                                                )}
                                                {detail.previewUrl && (
                                                    <button
                                                        type="button"
                                                        onClick={() => window.open(detail.previewUrl!, '_blank')}
                                                        className="mb-2 inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                                                    >
                                                        Open original
                                                        <OpenInNewRoundedIcon sx={{ fontSize: 13 }} />
                                                    </button>
                                                )}
                                            </>
                                        )}
                                        <p className="mt-1 break-all text-xs text-slate-600 dark:text-slate-300">
                                            {selectedJob.filename || selectedJob.blob_uri}
                                        </p>
                                        <p className="mt-1 break-all font-mono text-[11px] text-slate-400 dark:text-slate-500">
                                            sha256 {selectedJob.content_hash}
                                        </p>
                                    </div>

                                    {/* Right — the pipeline as NODES: what ran, who ran it, when it started/returned,
                                        whether it was skipped or never reached, and (expandable) its real input/output. */}
                                    <div className="p-4 md:h-full md:overflow-y-auto">
                                        <div className="mb-1 flex items-center gap-1.5">
                                            <p className="text-xs font-semibold text-slate-700 dark:text-slate-300">Pipeline</p>
                                            <Tooltip
                                                title="One card per logical processing node (Enqueue, OCR, Extract, Persist) — not per raw event. Each shows who executed it, when it started and returned, whether it succeeded, failed, was deliberately skipped, or was never reached, plus every real attempt underneath (e.g. each OCR reader tried, or each batch submit/poll/reconcile phase). Expand a node for its input/output."
                                                arrow
                                            >
                                                <span className="cursor-help text-[13px] leading-none text-slate-400 dark:text-slate-500">ⓘ</span>
                                            </Tooltip>
                                            {pipelineNodes.length > 0 &&
                                                (() => {
                                                    const first = pipelineNodes.find((n) => n.startedAt)
                                                    const last = [...pipelineNodes].reverse().find((n) => n.endedAt)
                                                    if (!first?.startedAt || !last?.endedAt) return null
                                                    const totalMs = new Date(last.endedAt).getTime() - new Date(first.startedAt).getTime()
                                                    return (
                                                        <span className="text-[11px] text-slate-400 dark:text-slate-500">
                                                            · {formatDuration(totalMs)} total
                                                        </span>
                                                    )
                                                })()}
                                            {eventsLoading && <CircularProgress size="sm" />}
                                            {!events && !eventsLoading && !eventsError && (
                                                <button
                                                    type="button"
                                                    onClick={() => loadEvents(selectedJob.id)}
                                                    className="rounded-lg border border-slate-300 px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-800"
                                                >
                                                    Load
                                                </button>
                                            )}
                                        </div>

                                        {eventsError && (
                                            <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 p-3 dark:border-amber-500/30 dark:bg-amber-500/10">
                                                <p className="text-xs text-amber-700 dark:text-amber-400">{eventsError}</p>
                                                <button
                                                    type="button"
                                                    onClick={() => loadEvents(selectedJob.id)}
                                                    className="mt-1.5 rounded-lg border border-slate-300 px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-800"
                                                >
                                                    Retry
                                                </button>
                                            </div>
                                        )}

                                        {events && events.length === 0 && (
                                            <p className="text-xs text-slate-400 dark:text-slate-500">No pipeline events recorded yet.</p>
                                        )}

                                        {pipelineNodes.map((node, i) => {
                                            const isLast = i === pipelineNodes.length - 1
                                            const isExpanded = expandedStages.has(node.key)
                                            const badgeTone = nodeStatusTones[node.status]
                                            return (
                                                <div key={node.key} className="flex gap-3">
                                                    <div className="flex w-5 shrink-0 flex-col items-center">
                                                        <NodeStatusIcon status={node.status} className={toneDotClasses[badgeTone]} />
                                                        {!isLast && <div className="my-0.5 w-px flex-1 bg-slate-200 dark:bg-slate-700" />}
                                                    </div>
                                                    <div className={`min-w-0 flex-1 ${isLast ? 'pb-0' : 'pb-3'}`}>
                                                        <button
                                                            type="button"
                                                            onClick={() => toggleStage(node.key)}
                                                            aria-expanded={isExpanded}
                                                            className="flex w-full items-start gap-1.5 rounded-md py-0.5 text-left hover:bg-slate-50 dark:hover:bg-slate-800/60"
                                                        >
                                                            <ExpandMoreRoundedIcon
                                                                className={`mt-0.5 shrink-0 text-slate-400 transition-transform dark:text-slate-500 ${isExpanded ? 'rotate-0' : '-rotate-90'}`}
                                                                sx={{ fontSize: 16 }}
                                                            />
                                                            <span className="min-w-0 flex-1">
                                                                <span className="flex flex-wrap items-baseline gap-x-2">
                                                                    <span className="text-xs font-semibold text-slate-900 dark:text-slate-100">
                                                                        {node.name}
                                                                    </span>
                                                                    <span
                                                                        className={`whitespace-nowrap rounded-full border px-1.5 py-0.5 text-[10px] font-medium ${toneBadgeClasses[badgeTone]}`}
                                                                    >
                                                                        {nodeStatusLabels[node.status]}
                                                                    </span>
                                                                    {node.durationMs !== null && (
                                                                        <span
                                                                            className="whitespace-nowrap rounded-full border border-indigo-200 bg-indigo-50 px-1.5 py-0.5 text-[10px] font-medium text-indigo-600 dark:border-indigo-500/30 dark:bg-indigo-500/10 dark:text-indigo-400"
                                                                            title="Time from this node starting to it returning"
                                                                        >
                                                                            {formatDuration(node.durationMs)}
                                                                        </span>
                                                                    )}
                                                                    {node.attempts.length > 1 && (
                                                                        <span className="whitespace-nowrap text-[10px] text-slate-400 dark:text-slate-500">
                                                                            {node.attempts.length} events
                                                                        </span>
                                                                    )}
                                                                    <span className="ml-auto whitespace-nowrap text-[11px] text-slate-400 dark:text-slate-500">
                                                                        {node.startedAt ? formatToUserLocalTime(node.startedAt) : ''}
                                                                    </span>
                                                                </span>
                                                                {!isExpanded && (
                                                                    <span className="block truncate text-[11px] text-slate-400 dark:text-slate-500">
                                                                        {node.status === 'skipped'
                                                                            ? `skipped: ${node.skipReason}`
                                                                            : node.status === 'not-reached'
                                                                              ? 'the job never reached this node'
                                                                              : node.executor
                                                                                ? `executor: ${node.executor}`
                                                                                : ' '}
                                                                    </span>
                                                                )}
                                                            </span>
                                                        </button>

                                                        {isExpanded && (
                                                            <div className="pl-[22px]">
                                                                {node.executor && (
                                                                    <p className="text-[11px] text-slate-400 dark:text-slate-500">
                                                                        executor: <span className="font-mono">{node.executor}</span>
                                                                    </p>
                                                                )}
                                                                {node.status === 'skipped' && (
                                                                    <p className="text-[11px] text-amber-600 dark:text-amber-400">
                                                                        Skipped — {node.skipReason}
                                                                    </p>
                                                                )}
                                                                {node.status === 'not-reached' && (
                                                                    <p className="text-[11px] text-slate-400 dark:text-slate-500">
                                                                        The job never reached this node
                                                                        {failureToStatuses.has(selectedJob.status)
                                                                            ? ' — the pipeline ended before it got here.'
                                                                            : terminalStatuses.has(selectedJob.status)
                                                                              ? '.'
                                                                              : ' yet — still in progress.'}
                                                                    </p>
                                                                )}

                                                                {/* Node-level input/output — the actual OCR text or extracted JSON, expandable
                                                                    independently since it can be long. Sourced from document_extractions
                                                                    (via `detail`), not from the event log — one row per content hash,
                                                                    regardless of which/how-many events exist for this job. */}
                                                                {node.key === 'ocr' && (detail?.ocrText || detail?.job.ocr_provider) && (
                                                                    <div className="mt-2 overflow-hidden rounded-lg border border-slate-200 dark:border-slate-700">
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => toggleStage('artifact-ocr')}
                                                                            aria-expanded={expandedStages.has('artifact-ocr')}
                                                                            className="flex w-full items-center gap-1.5 bg-slate-50 px-2.5 py-1.5 text-left hover:bg-slate-100 dark:bg-slate-800/60 dark:hover:bg-slate-800"
                                                                        >
                                                                            <ExpandMoreRoundedIcon
                                                                                className={`shrink-0 text-slate-400 transition-transform dark:text-slate-500 ${expandedStages.has('artifact-ocr') ? 'rotate-0' : '-rotate-90'}`}
                                                                                sx={{ fontSize: 16 }}
                                                                            />
                                                                            <span className="text-[11px] font-medium text-slate-600 dark:text-slate-300">
                                                                                Output{detail?.job.ocr_provider ? ` · ${detail.job.ocr_provider}` : ''}
                                                                                {detail?.job.page_count ? ` · ${detail.job.page_count}p` : ''}
                                                                            </span>
                                                                        </button>
                                                                        {expandedStages.has('artifact-ocr') && (
                                                                            <div className="p-2.5">
                                                                                <p className="mb-1.5 text-[11px] italic text-slate-400 dark:text-slate-500">
                                                                                    The text this node produced from the source document — this becomes the
                                                                                    Extract node&apos;s input.
                                                                                </p>
                                                                                {detail?.ocrText ? (
                                                                                    <div
                                                                                        className="max-h-64 overflow-auto rounded-md border border-slate-100 dark:border-slate-800"
                                                                                        data-color-mode={isDarkMode ? 'dark' : 'light'}
                                                                                    >
                                                                                        <MDEditor.Markdown
                                                                                            source={detail.ocrText}
                                                                                            style={{ fontSize: 12.5, background: 'transparent', padding: 8 }}
                                                                                        />
                                                                                    </div>
                                                                                ) : (
                                                                                    <p className="text-[11px] text-slate-400 dark:text-slate-500">
                                                                                        OCR ran but produced no readable text.
                                                                                    </p>
                                                                                )}
                                                                                {detail?.job.ocr_blocks && detail.job.ocr_blocks.length > 0 && (
                                                                                    <div className="mt-2 max-h-48 overflow-auto rounded-md bg-slate-50 p-2 dark:bg-slate-800/60">
                                                                                        <p className="mb-1.5 text-[11px] text-slate-500 dark:text-slate-400">
                                                                                            structured blocks ({detail.job.ocr_blocks.length})
                                                                                        </p>
                                                                                        <div className="flex flex-col gap-1.5">
                                                                                            {detail.job.ocr_blocks.map((block, bi) => (
                                                                                                <div key={bi} className="flex items-start gap-1.5">
                                                                                                    <span className="shrink-0 rounded border border-slate-200 px-1.5 py-0.5 text-[10px] text-slate-500 dark:border-slate-600 dark:text-slate-400">
                                                                                                        p{block.page}
                                                                                                    </span>
                                                                                                    <span
                                                                                                        className={`shrink-0 rounded-full border px-1.5 py-0.5 text-[10px] font-medium ${
                                                                                                            toneBadgeClasses[
                                                                                                                block.kind === 'table'
                                                                                                                    ? 'primary'
                                                                                                                    : block.kind === 'heading'
                                                                                                                      ? 'success'
                                                                                                                      : block.kind === 'figure'
                                                                                                                        ? 'warning'
                                                                                                                        : 'neutral'
                                                                                                            ]
                                                                                                        }`}
                                                                                                    >
                                                                                                        {block.kind}
                                                                                                    </span>
                                                                                                    <span className="font-mono text-[11px] text-slate-600 dark:text-slate-300">
                                                                                                        {truncateText(block.content, 120)}
                                                                                                    </span>
                                                                                                </div>
                                                                                            ))}
                                                                                        </div>
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                )}

                                                                {node.key === 'extract' && detail?.job.extracted_data && (
                                                                    <div className="mt-2 overflow-hidden rounded-lg border border-slate-200 dark:border-slate-700">
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => toggleStage('artifact-extraction')}
                                                                            aria-expanded={expandedStages.has('artifact-extraction')}
                                                                            className="flex w-full items-center gap-1.5 bg-slate-50 px-2.5 py-1.5 text-left hover:bg-slate-100 dark:bg-slate-800/60 dark:hover:bg-slate-800"
                                                                        >
                                                                            <ExpandMoreRoundedIcon
                                                                                className={`shrink-0 text-slate-400 transition-transform dark:text-slate-500 ${expandedStages.has('artifact-extraction') ? 'rotate-0' : '-rotate-90'}`}
                                                                                sx={{ fontSize: 16 }}
                                                                            />
                                                                            <span className="text-[11px] font-medium text-slate-600 dark:text-slate-300">
                                                                                Output
                                                                                {detail?.job.extract_model ? ` · ${detail.job.extract_model}` : ''}
                                                                                {detail?.job.extracted_doc_type
                                                                                    ? ` · resolved: ${detail.job.extracted_doc_type}`
                                                                                    : ''}
                                                                            </span>
                                                                        </button>
                                                                        {expandedStages.has('artifact-extraction') && (
                                                                            <div className="p-2.5">
                                                                                <p className="mb-1.5 text-[11px] italic text-slate-400 dark:text-slate-500">
                                                                                    Input:{' '}
                                                                                    {detail?.job.extracted_extraction_mode === 'visual'
                                                                                        ? 'the source document/image directly (visual mode — no OCR text).'
                                                                                        : 'the OCR node’s output above.'}{' '}
                                                                                    Output: the structured JSON this node returned — what gets persisted as the
                                                                                    job&apos;s final result.
                                                                                </p>
                                                                                <pre className="max-h-64 overflow-auto whitespace-pre-wrap rounded-md border border-slate-100 p-2 font-mono text-[11px] text-slate-600 dark:border-slate-800 dark:text-slate-300">
                                                                                    {JSON.stringify(detail.job.extracted_data, null, 2)}
                                                                                </pre>
                                                                                {!!detail.job.extracted_coverage?.length && (
                                                                                    <div className="mt-2">
                                                                                        <p className="mb-1 text-[11px] font-medium text-slate-600 dark:text-slate-300">
                                                                                            Coverage — expected facts the read left empty
                                                                                        </p>
                                                                                        <ul className="flex flex-col gap-0.5 font-mono text-[11px] text-slate-600 dark:text-slate-300">
                                                                                            {detail.job.extracted_coverage.map((c) => (
                                                                                                <li
                                                                                                    key={c.field}
                                                                                                    className={
                                                                                                        c.verdict === 'likely_missed'
                                                                                                            ? 'text-amber-600 dark:text-amber-400'
                                                                                                            : ''
                                                                                                    }
                                                                                                >
                                                                                                    {c.field}: {c.verdict}
                                                                                                </li>
                                                                                            ))}
                                                                                        </ul>
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                )}

                                                                {/* Every real attempt underneath this node — OCR ladder rungs, batch
                                                                    submit/poll/reconcile phases, or the raw enqueue/persist transition. */}
                                                                {node.attempts.length > 0 && (
                                                                    <div className="mt-2 flex flex-col gap-1">
                                                                        {node.attempts.map((a) => (
                                                                            <div
                                                                                key={a.key}
                                                                                className="flex items-start gap-1.5 rounded-md bg-slate-50 px-2 py-1 dark:bg-slate-800/60"
                                                                            >
                                                                                <ToneIcon
                                                                                    tone={a.tone}
                                                                                    className={`mt-0.5 shrink-0 ${toneDotClasses[a.tone]}`}
                                                                                />
                                                                                <div className="min-w-0 flex-1">
                                                                                    <div className="flex flex-wrap items-baseline gap-x-2">
                                                                                        <span className="text-[11px] font-medium text-slate-700 dark:text-slate-300">
                                                                                            {a.label}
                                                                                        </span>
                                                                                        <span className="ml-auto whitespace-nowrap text-[10px] text-slate-400 dark:text-slate-500">
                                                                                            {formatToUserLocalTime(a.at)}
                                                                                        </span>
                                                                                    </div>
                                                                                    <p className="text-[10px] text-slate-400 dark:text-slate-500">
                                                                                        actor: {a.actor || 'N/A'}
                                                                                    </p>
                                                                                    {a.detail && Object.keys(a.detail).length > 0 && (
                                                                                        <pre className="mt-0.5 whitespace-pre-wrap font-mono text-[10px] text-slate-400 dark:text-slate-500">
                                                                                            {JSON.stringify(a.detail, null, 2)}
                                                                                        </pre>
                                                                                    )}
                                                                                </div>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            )
                                        })}
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </ModalDialog>
            </Modal>
        </React.Fragment>
    )
}

/** One row's batch: a colored chip shared by every job in it, plus the batch's model, files sent and tokens. */
function BatchCell({ row, onSelect }: { row: DocumentJobListRow; onSelect: (batchId: string) => void }) {
    const batchId = row.batch_id ?? row.leader_batch_id
    if (!batchId) return <Typography level="body-xs">—</Typography>
    const color = batchColor(batchId)
    const follower = !row.batch_id
    const files = row.batch_size ?? row.batch_jobs ?? 0
    return (
        <Tooltip
            title={
                <Box sx={{ fontSize: 12 }}>
                    <div>Batch {batchId}</div>
                    <div>Model {row.batch_model ?? 'unknown'}</div>
                    <div>
                        {files} file{files === 1 ? '' : 's'} sent · {row.batch_jobs ?? 0} still in it
                    </div>
                    <div>
                        {Number(row.batch_input_tokens ?? 0).toLocaleString()} input · {Number(row.batch_output_tokens ?? 0).toLocaleString()} output tokens
                    </div>
                    {follower && <div>This job reused its leader's result from that batch.</div>}
                </Box>
            }
            arrow
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.25, cursor: 'default', minWidth: 0, overflow: 'hidden' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, maxWidth: '100%' }}>
                    <Chip
                        size="sm"
                        variant={follower ? 'outlined' : 'solid'}
                        onClick={() => onSelect(batchId)}
                        sx={{
                            cursor: 'pointer',
                            fontFamily: 'monospace',
                            bgcolor: follower ? 'transparent' : color,
                            borderColor: color,
                            color: follower ? color : '#fff',
                        }}
                    >
                        {follower ? '↳ ' : ''}
                        {batchId.slice(0, 6)}
                    </Chip>
                    <Typography level="body-xs" sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                        {files} files
                    </Typography>
                </Box>
                <Typography
                    level="body-xs"
                    sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}
                >
                    {row.batch_model ?? '?'} · {formatTokens(row.batch_input_tokens)} → {formatTokens(row.batch_output_tokens)}
                </Typography>
            </Box>
        </Tooltip>
    )
}
